import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, TextField, Button, Typography, Alert, CircularProgress,
  InputAdornment, IconButton, Fade, useMediaQuery, Divider,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import WindowIcon from '@mui/icons-material/Window';
import { useAuth } from '../utils/AuthContext';
import api from '../utils/api';

const cardSx = {
  width: '100%',
  maxWidth: { xs: '360px', sm: '430px' },
  px: { xs: 2.25, sm: 4 },
  pt: { xs: 2.5, sm: 3 },
  pb: { xs: 3, sm: 6.5 },
  backgroundColor: 'rgba(255,255,255,0.82)',
  borderRadius: { xs: '24px', sm: '20px' },
  boxShadow: { xs: '0 14px 40px rgba(0,0,0,0.22)', sm: '0 20px 60px rgba(0,0,0,0.30)' },
  textAlign: 'center',
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  boxSizing: 'border-box',
  border: '1px solid rgba(255,255,255,0.38)',
};

const btnSx = {
  borderRadius: '10px', fontWeight: 700, textTransform: 'none', py: 1.5,
  backgroundColor: '#2f63ad', boxShadow: 'none',
  '&:hover': { backgroundColor: '#1f4f94', boxShadow: 'none' },
};

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.97)' } };

const SSO_ERROR_MSGS = {
  no_user:      'No admin account found for that Microsoft email. Contact your administrator.',
  auth_failed:  'Microsoft sign-in failed. Please try again.',
  invalid_state:'Session expired during SSO. Please try again.',
  default:      'Microsoft sign-in failed.',
};

export default function Login() {
  const { loginStep1, loginStep2 } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const isMobile  = useMediaQuery('(max-width:600px)');

  const [step, setStep]           = useState('password');
  const [partialToken, setPartialToken] = useState('');
  const [form, setForm]           = useState({ username: '', password: '' });
  const [code, setCode]           = useState('');
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [showPw, setShowPw]       = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);

  // Check SSO status + SSO error from redirect
  useEffect(() => {
    api.get('/auth/sso-status').then(r => setSsoEnabled(r.data.ssoEnabled)).catch(() => {});
    const params = new URLSearchParams(location.search);
    const ssoErr = params.get('sso_error');
    if (ssoErr) setError(SSO_ERROR_MSGS[ssoErr] || SSO_ERROR_MSGS.default);
  }, [location.search]);

  const handlePassword = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const result = await loginStep1(form.username, form.password);
      if (result.mfaRequired) {
        setPartialToken(result.partialToken);
        setStep('mfa');
      } else {
        // mfaSetupRequired - AuthContext already set user, App will redirect to /mfa-setup
        navigate('/mfa-setup');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMfa = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await loginStep2(partialToken, code);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid code');
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100dvh', width: '100%', position: 'relative', overflow: 'hidden', backgroundColor: '#eef2f7' }}>
      <Box sx={{
        position: 'absolute', inset: 0,
        backgroundImage: isMobile ? "url('/hoa_mobile_9x16.jpg')" : "url('/hoa.jpg')",
        backgroundSize: 'cover', backgroundPosition: isMobile ? 'center top' : 'center center',
        filter: isMobile ? 'blur(4px)' : 'blur(6px)', transform: 'scale(1.05)',
      }} />
      <Box sx={{ position: 'absolute', inset: 0, backdropFilter: 'blur(3.5px)',
        background: 'linear-gradient(180deg, rgba(13,24,43,0.14), rgba(13,24,43,0.34))' }} />

      <Box sx={{ position: 'relative', zIndex: 2, minHeight: '100dvh', display: 'flex',
        justifyContent: 'center', alignItems: { xs: 'flex-start', sm: 'center' },
        px: { xs: 1.5, sm: 2 }, pt: { xs: 7, sm: 3 }, pb: { xs: 4, sm: 3 } }}>
        <Fade in timeout={500}>
          <Box sx={cardSx}>
            {/* Logo */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 1.1, sm: 1.5 }, mt: '0.15rem' }}>
              <Box component="img" src="/img/logo.png" alt="Logo"
                sx={{ width: { xs: '145px', sm: '220px' }, maxWidth: '100%', height: 'auto', objectFit: 'contain' }} />
            </Box>
            <Typography variant="body2" sx={{ color: '#6b778c', mb: { xs: 1.8, sm: 2.5 }, fontSize: { xs: '0.78rem', sm: '0.85rem' } }}>
              Admin Control Panel
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', textAlign: 'left' }}>{error}</Alert>}

            {step === 'password' && (
              <form onSubmit={handlePassword}>
                <TextField fullWidth label="Username or Email" value={form.username} autoFocus
                  onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
                  sx={{ mb: 2, ...fieldSx }} />
                <TextField fullWidth label="Password" type={showPw ? 'text' : 'password'}
                  value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  InputProps={{ endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPw(v => !v)} edge="end">
                        {showPw ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )}}
                  sx={{ mb: 3, ...fieldSx }} />
                <Button fullWidth type="submit" variant="contained" size="large" disabled={loading} sx={btnSx}>
                  {loading ? <CircularProgress size={22} color="inherit" /> : 'Sign In'}
                </Button>
                {ssoEnabled && (
                  <>
                    <Divider sx={{ my: 2, color: 'text.secondary', fontSize: '0.75rem' }}>or</Divider>
                    <Button
                      fullWidth variant="outlined" size="large"
                      startIcon={<WindowIcon />}
                      href="/api/auth/microsoft"
                      sx={{ borderRadius: '10px', fontWeight: 600, textTransform: 'none', borderColor: 'rgba(0,0,0,0.18)', color: '#444', backgroundColor: '#fff', '&:hover': { backgroundColor: '#f5f5f5', borderColor: 'rgba(0,0,0,0.3)' } }}
                    >
                      Sign in with Microsoft
                    </Button>
                  </>
                )}
              </form>
            )}

            {step === 'mfa' && (
              <form onSubmit={handleMfa}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <LockIcon sx={{ fontSize: 40, color: '#2f63ad' }} />
                </Box>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Enter the 6-digit code from your authenticator app.
                </Typography>
                <TextField
                  fullWidth
                  label="Authenticator Code"
                  value={code}
                  autoFocus
                  inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  sx={{ mb: 3, ...fieldSx }}
                />
                <Button fullWidth type="submit" variant="contained" size="large"
                  disabled={loading || code.length < 6} sx={btnSx}>
                  {loading ? <CircularProgress size={22} color="inherit" /> : 'Verify'}
                </Button>
                <Button fullWidth variant="text" size="small" sx={{ mt: 1, color: '#6b778c' }}
                  onClick={() => { setStep('password'); setCode(''); setError(''); }}>
                  ← Back to login
                </Button>
              </form>
            )}
          </Box>
        </Fade>
      </Box>
    </Box>
  );
}
